import { useLocalStorage, useSearchParam } from 'react-use';
import { useQuery } from 'react-query';
import { useMemo } from 'react';
import {
  DEFAULT_WORKER_ID,
  LOCAL_STORAGE_KEY,
  SEARCH_QUERY_KEYS,
  SpecialShape,
} from '../constants';
import { LocalStorageWorkerIdKey } from '../@types';
import { api, METHOD } from './api';
import { Color } from '../constants/Color';
import rgb from './rgb';

export const useWorkerId = () => {
  return useSearchParam(SEARCH_QUERY_KEYS.WORKER_ID) ?? DEFAULT_WORKER_ID;
};

export const useWorkerLocalStorage = () =>
  useLocalStorage<LocalStorageWorkerIdKey>(LOCAL_STORAGE_KEY.WORKER_ID, {
    seriesNo: undefined,
    savedRuleGuess: undefined,
  });

export const useExperimentPlan = () => useSearchParam(SEARCH_QUERY_KEYS.EXPERIMENT_PLAN) ?? '';

export const useColorRgb = (color?: Color) => {
  const { data: colorMapping } = useQuery(`COLOR-MAPPING`, () =>
    api('/game-data/GameService2/colorMap', METHOD.GET, undefined, {}).then((response) => {
      if (response.data.error) {
        throw new Error(response.data.errmsg);
      }

      return response.data;
    }),
  );

  if (colorMapping && color) {
    const [r, g, b] = colorMapping[color];
    return rgb(r, g, b);
  }
};

export const useColorMapAndShape = () => {
  const { data } = useQuery(`COLOR-MAP-AND-SHAPES`, () =>
    Promise.all([
      api('/game-data/GameService2/colorMap', METHOD.GET, undefined, {}).then((response) => {
        if (response.data.error) {
          throw new Error(response.data.errmsg);
        }
        return Object.entries(response.data).reduce<{ [colorName: string]: string }>(
          (acc, [key, value]) => ({
            ...acc,
            ...(key !== 'errmsg' &&
              key !== 'error' && {
                [key]: rgb(value[0], value[1], value[2]),
              }),
          }),
          {},
        );
      }),
      api('/game-data/GameService2/listShapes', METHOD.GET, undefined, {}).then((response) => {
        return response.data.values.filter(
          (key) => !Object.values(SpecialShape).includes(key as SpecialShape),
        );
      }),
    ]),
  );

  let colorMap: { [colorName: string]: string } | undefined;
  let shapes: string[] | undefined;
  if (data) {
    [colorMap, shapes] = data;
  }

  return { colorMap, shapes };
};

export const useDefaultColorAndShape = () => {
  const { colorMap, shapes } = useColorMapAndShape();
  const color = useMemo(() => (colorMap ? Object.keys(colorMap).sort()[0] : undefined), [colorMap]);

  return { color, shape: shapes?.[0] };
};
